import * as React from 'react'
import { Site } from '@envato/foundation-types'

import ArticleBlock from '../../components/categoryPage/articleBlock'
import CategoriesWithIconsBlock from '../../components/categoryPage/categoriesWithIconsBlock'
import CategoriesWithCountBlock from '../../components/categoryPage/categoriesWithCountBlock'
import CategoryIconsBlock from '../../components/categoryPage/categoryIconsBlock'
import PopularSearchesBlock from '../../components/categoryPage/popularSearchesBlock'
import CategoryPageWrapper from '../../components/categoryPage/categoryPageWrapper'
import PromoItemsBlock from '../../components/categoryPage/promoItemsBlock'
import ElementsBannerBlock from '../../components/categoryPage/elementsBannerBlock'
import ElementsPromoItemsBlock from '../../components/categoryPage/elementsPromoItemsBlock'
import SearchFiltersBlock from '../../components/categoryPage/searchFiltersBlock'
import FullWidthBannerBlock from '../../components/categoryPage/fullWidthBannerBlock'
import ItemsBlock from '../../components/categoryPage/itemsBlock'
import SearchBlock from '../../components/categoryPage/searchBlock'
import SearchBannersWithIconsBlock from '../../components/categoryPage/searchBannersWithIconsBlock'
import TextSearchFiltersBlock from '../../components/categoryPage/textSearchFiltersBlock'
import EmptyResultsPlaceholder from '../../components/emptyResultsPlaceholder'
import PageHeader from '../../components/pageHeader'
import SurveyBanner from '../../components/surveyBanners/surveyBanner'
import TabbedTableBlock from '../../components/categoryPage/tabbedTableBlock'
import { Props } from '../../containers/categoryPageContainer/categoryPageContainer'
import { getPageViewCount } from '../../../common/utils/tracking/trackPageViewCount'
import useDidMount from '../../../common/hooks/useDidMount'
import { hasFeature } from '../../../common/utils/features'
import { getConfigBySite } from '../../../config/sites'
import {
  shouldShowBlock,
  isNotEmpty,
  selectBlocksFromActiveFeatureFlip
} from '../../../common/utils/pageBlocks'

import {
  IBlockComponentsMap,
  IRenderCategoryPageBlocks
} from '../../../typings/pageBlocks'
import { IExperimentsState } from '../../../common/reducers/experiments'
import { ICommonLocation } from '../../../common/utils/location/types'

const categoryLookupMap: IBlockComponentsMap = {
  ItemsBlock,
  ArticleBlock,
  CategoriesWithIconsBlock,
  CategoriesWithCountBlock,
  CategoryIconsBlock,
  SearchBlock,
  SearchBannersWithIconsBlock,
  PopularSearchesBlock,
  PromoItemsBlock,
  ElementsBannerBlock,
  ElementsPromoItemsBlock,
  SearchFiltersBlock,
  TextSearchFiltersBlock,
  FullWidthBannerBlock,
  TabbedTableBlock,
  EnvatoPromoItemsBlock: PromoItemsBlock
}

const DummyIconsWithToggle = () => <h2>Icons Navigation with toggle</h2>
const DummyIconsWithoutToggle = () => <h2>Icons Navigation without toggle</h2>
const DummyTextWithToggle = () => <h2>Text Navigation with toggle</h2>
const DummyTextWithoutToggle = () => <h2>Text Navigation without toggle</h2>

const getComponent = (
  componentType: string,
  lookupMap: IBlockComponentsMap,
  currentSite: Site,
  location: ICommonLocation,
  experiments: IExperimentsState | undefined
) => {
  if (componentType !== 'CategoriesWithIconsBlock') {
    return lookupMap[componentType]
  } else {
    const showSmallIconsNav = hasFeature(
      'smallIconsNavigation',
      currentSite,
      location,
      experiments
    )
    const showTextTableNav = hasFeature(
      'textTableNavigation',
      currentSite,
      location,
      experiments
    )
    const showViewMoreToggle = hasFeature(
      'viewMoreToggle',
      currentSite,
      location,
      experiments
    )

    if (showSmallIconsNav && showViewMoreToggle) {
      return DummyIconsWithToggle
    } else if (showSmallIconsNav) {
      return DummyIconsWithoutToggle
    } else if (showTextTableNav && showViewMoreToggle) {
      return DummyTextWithToggle
    } else if (showTextTableNav) {
      return DummyTextWithoutToggle
    } else {
      return lookupMap[componentType]
    }
  }
}

export const renderBlocks = ({
  blocks,
  currentSite,
  location,
  locationParams,
  makeSearchLink,
  lookupMap = categoryLookupMap,
  permitScheduledBlocks,
  experiments
}: IRenderCategoryPageBlocks) => {
  return blocks
    .filter(block =>
      shouldShowBlock({ block, currentSite, location, permitScheduledBlocks })
    )
    .map((block, index) => {
      const MatchedComponent = getComponent(
        block.componentType,
        lookupMap,
        currentSite,
        location,
        experiments
      )

      return MatchedComponent ? (
        <MatchedComponent
          key={block.componentType + index}
          currentSite={currentSite}
          location={location}
          locationParams={locationParams}
          makeSearchLink={makeSearchLink}
          {...block}
        />
      ) : null
    })
    .filter(isNotEmpty)
}

const renderSurveyBanner = (currentSite: Site) => {
  const survey = getConfigBySite(currentSite, 'surveys.category')
  const shortName = getConfigBySite(currentSite, 'abbreviation').toLowerCase()

  if (
    !!survey &&
    getPageViewCount(currentSite, 'category') >= survey.minSearchCountToShow
  ) {
    return (
      <SurveyBanner
        siteId={shortName}
        pageType="category"
        surveyUrl={survey.url}
        surveyText={survey.text}
        surveyLink={survey.linkText}
      />
    )
  }

  return null
}

const CategoryPage = ({
  location,
  metadata,
  topArticle,
  category,
  blocks,
  blocksB,
  blocksC,
  blocksD,
  currentSite,
  makeSearchLink,
  experiments,
  experimentsReady,
  locationParams,
  dataRequestFailed,
  isLoading
}: Props) => {
  const selectedBlocks = selectBlocksFromActiveFeatureFlip({
    page: 'category',
    data: { blocks, blocksB, blocksC, blocksD },
    location,
    currentSite,
    experiments
  })

  const shouldShowError =
    !isLoading && (dataRequestFailed || !selectedBlocks.length)

  const permitScheduledBlocks = useDidMount()

  return (
    <CategoryPageWrapper
      experimentsReady={experimentsReady}
      isLoading={isLoading}
      currentSite={currentSite}
    >
      {!shouldShowError && metadata && (
        <PageHeader
          heading={metadata.heading}
          subHeading={metadata.sub_heading}
          topArticle={topArticle}
          category={category}
          currentSite={currentSite}
          locationParams={locationParams}
          makeSearchLink={makeSearchLink}
          showAutoSuggest
        />
      )}

      {shouldShowError && (
        <EmptyResultsPlaceholder
          heading="We're experiencing a technical issue"
          subHeading="Please refresh or try again later"
        />
      )}

      {renderSurveyBanner(currentSite)}

      {!shouldShowError &&
        renderBlocks({
          blocks: selectedBlocks,
          currentSite,
          location,
          locationParams,
          makeSearchLink,
          permitScheduledBlocks,
          experiments
        })}
    </CategoryPageWrapper>
  )
}

export default CategoryPage
